import React from 'react';
import { Button } from 'equasion-lib';
import { FiAlertTriangle, FiCheckCircle, FiSearch, FiSlash } from 'react-icons/fi';
import { zoneTypeLabel } from './useZoneScanSession';
import type { ZoneScanStatus } from './useZoneScanSession';
import type { ScannedZone } from './zoneByScanCode';

interface ZoneScanBannerProps {
  status: ZoneScanStatus;
  rejectionFor: (zone: ScannedZone) => string | null;
  onChoose: (zone: ScannedZone) => void;
  onScanAgain: () => void;
  /** Laid out under a small in-form viewport instead of floating over a full-screen one. */
  inline?: boolean;
}

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

/** What the zone scanner says about the last label: looking it up, accepted, or why not. */
const ZoneScanBanner = ({
  status,
  rejectionFor,
  onChoose,
  onScanAgain,
  inline = false
}: ZoneScanBannerProps) => {
  const className = (...modifiers: string[]) =>
    ['zone-scan__banner', ...modifiers, inline && 'zone-scan__banner--inline']
      .filter(Boolean)
      .join(' ');

  switch (status.kind) {
    case 'resolving':
      return (
        <div className={className('zone-scan__banner--busy')} role="status">
          <FiSearch />
          <div>
            <strong>Looking up “{status.code}”…</strong>
          </div>
        </div>
      );
    case 'matched':
      return (
        <div className={className('zone-scan__banner--ok')} role="status">
          <FiCheckCircle />
          <div>
            <strong>{status.zone.name}</strong>
            <span>{zoneTypeLabel(status.zone.zoneType)} zone selected</span>
            {/* A normalized-name match ignored separators, so the label and the zone name
                are not character-identical. Show both — that is exactly where a look-alike
                label would slip through unnoticed. */}
            {status.matchedOn === 'normalizedName' && !!status.code && (
              <span className="zone-scan__code">matched label “{status.code}”</span>
            )}
          </div>
        </div>
      );
    case 'notFound':
      return (
        <div className={className('zone-scan__banner--bad')} role="alert">
          <FiAlertTriangle />
          <div>
            <strong>No zone matches that label</strong>
            <span className="zone-scan__code">{status.code || '(unreadable code)'}</span>
          </div>
          <Button type="secondary" {...click(onScanAgain)}>
            Scan again
          </Button>
        </div>
      );
    case 'rejected':
      return (
        <div className={className('zone-scan__banner--bad')} role="alert">
          <FiSlash />
          <div>
            <strong>Can’t use that zone</strong>
            <span>{status.message}</span>
          </div>
          <Button type="secondary" {...click(onScanAgain)}>
            Scan again
          </Button>
        </div>
      );
    case 'ambiguous':
      return (
        <div
          className={className('zone-scan__banner--bad', 'zone-scan__banner--stacked')}
          role="alert"
        >
          <div className="zone-scan__banner-head">
            <FiAlertTriangle />
            <div>
              <strong>More than one zone matches</strong>
              <span className="zone-scan__code">{status.code}</span>
            </div>
          </div>
          <div className="zone-scan__choices">
            {status.matches.map((zone) => {
              const rejection = rejectionFor(zone);
              return (
                <button
                  key={zone.id}
                  type="button"
                  className="zone-scan__choice"
                  disabled={!!rejection}
                  onClick={() => onChoose(zone)}
                >
                  <span className="zone-scan__choice-name">{zone.name}</span>
                  {/* Two zones can share a name across locations, in which case the name
                      alone cannot tell them apart — the description usually can. */}
                  <span className="zone-scan__choice-meta">
                    {rejection ||
                      [`${zoneTypeLabel(zone.zoneType)} zone`, zone.description]
                        .filter(Boolean)
                        .join(' · ')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    default:
      return null;
  }
};

export default ZoneScanBanner;
